import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown } from "react-icons/fa";
import { AccountBalanceOutlined } from "@mui/icons-material";
import PartnerInfo from "../../../components/partner-info";

export type Option = {
  label: string;
  value: string;
  id?: number;
  mobile?: string;
  type?: string;
  profilePic?: string;
  address?: string;
  status?: number;
  sid?: number;
};

type Props = {
  options: Option[];
  selected: Option[];
  onChange: any;
  placeholder?: string;
  fromMainScreen?: boolean;
};

export default function MultiSelectDropdownForSoftwares({
  options,
  selected,
  onChange,
  placeholder = "Search...",
  fromMainScreen = false,
}: Props) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropUp, setDropUp] = useState(false);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    window.addEventListener("click", handleClickOutside, true);
    return () => window.removeEventListener("click", handleClickOutside, true);
  }, []);

  //  text for search
  const normalizeText = (str: string = "") =>
    str.toString().toLowerCase().replace(/\s+/g, "");

  const filtered = options.filter((opt) =>
    normalizeText(opt.label).includes(normalizeText(search))
  );

  // onselect
  const toggleSelect = (option: Option) => {
    const exists = selected.find((s) => s.value === option.value);
    let updated: Option[];

    if (exists) {
      if (exists.sid && exists.sid > 0) {
        if (exists.status === 0) {
          updated = selected.map((s) =>
            s.value === option.value ? { ...s, status: 2 } : s
          );
        } else {
          updated = selected.map((s) =>
            s.value === option.value ? { ...s, status: 0 } : s
          );
        }
      } else {
        updated = selected.filter((s) => s.value !== option.value);
      }
    } else {
      updated = [...selected, { ...option, sid: 0, status: 1 }];
    }

    onChange(updated);
  };

  // Remove selected crop
  const removeSelected = (value: string) => {
    const item = selected.find((s) => s.value === value);
    if (!item) return;

    let updated: Option[];
    if (!item.sid || item.sid === 0) {
      updated = selected.filter((s) => s.value !== value);
    } else {
      updated = selected.map((s) =>
        s.value === value ? { ...s, status: 0 } : s
      );
    }

    onChange(updated);
  };

  // Select all
  const toggleSelectAll = () => {
    const allSelected =
      selected.filter((s) => s.status !== 0).length === filtered.length;

    let updated: Option[] = [];

    if (allSelected) {
      updated = selected.reduce<Option[]>((acc, item) => {
        if (item.sid && item.sid > 0) {
          acc.push({ ...item, status: 0 });
        }
        return acc;
      }, []);
    } else {
      updated = filtered.map((item) => {
        const existing = selected.find((s) => s.value === item.value);

        if (existing) {
          if (existing.sid && existing.sid > 0) {
            return { ...existing, status: 2 };
          }

          return existing;
        }

        return { ...item, sid: 0, status: 1 };
      });
    }

    onChange(updated);
  };

  // cleek if dropdown open top
  useEffect(() => {
    if (open && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 240;
      setDropUp(spaceBelow < dropdownHeight);
    }
  }, [open]);

  return (
    <div ref={dropdownRef} className="relative w-full">
      <div
        className="border rounded-md flex items-center px-2 py-3 cursor-pointer bg-white gap-2"
        onClick={() => setOpen((prev) => !prev)}
      >
        {!open && (
          <div className="flex flex-wrap gap-1 flex-1 text-sm">
            {selected.filter((s) => s.status !== 0).length > 0 ? (
              selected
                .filter((s) => s.status !== 0)
                .slice(0, 2)
                .map((s) => (
                  <span
                    key={s.value}
                    className="bg-[#D7F3DD] rounded-full px-2 py-1 flex items-center gap-1"
                  >
                    {s.label}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSelected(s.value);
                      }}
                      className="color-red"
                    >
                      ✕
                    </button>
                  </span>
                ))
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
            {selected.filter((s) => s.status !== 0).length > 2 && (
              <span className="bg-[#D7F3DD] rounded-full px-2 py-1">
                +{selected.filter((s) => s.status !== 0).length - 2}
              </span>
            )}
          </div>
        )}
        {open && (
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={placeholder}
            className="flex-1 outline-none cursor-text text-sm"
          />
        )}
        <FaChevronDown
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          size={10}
          color="#16a34a"
        />
      </div>

      {open && (
        <div
          className={`absolute z-10 bg-white border rounded-md w-full shadow-lg ${
            dropUp ? "bottom-full mb-1" : "mt-1"
          }`}
        >
          {filtered.length > 0 && (
            <div
              className="px-3 py-2 flex items-center gap-2 cursor-pointer text-sm hover:bg-green-50 border-b"
              onClick={(e) => {
                e.stopPropagation();
                toggleSelectAll();
              }}
            >
              <input
                type="checkbox"
                checked={
                  selected.filter((s) => s.status !== 0).length ===
                    filtered.length && filtered.length > 0
                }
                readOnly
              />
              Select All
            </div>
          )}

          <div className="max-h-60 overflow-auto text-sm">
            {filtered.length > 0 ? (
              filtered.map((option) => {
                const isSelected = selected.some(
                  (s) => s.value === option.value && s.status !== 0
                );
                return (
                  <div
                    key={option.value}
                    className={`px-3 py-2 cursor-pointer flex items-center gap-2 border-b hover:bg-[#D7F3DD] ${
                      isSelected ? "bg-[#D7F3DD]" : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelect(option);
                    }}
                  >
                    <input type="checkbox" checked={isSelected} readOnly />
                    {fromMainScreen ? (
                      <div className="flex items-center gap-2 fs-14 color-text-primary font-semibold">
                        <AccountBalanceOutlined
                          style={{ fontSize: 22, color: "color-sub-text" }}
                        />
                        <span>{option.label}</span>
                      </div>
                    ) : (
                      <PartnerInfo
                        profilePic={option.profilePic || ""}
                        name={option.label}
                        shortName=""
                        partnerType={option.type}
                        partnerId={option.id}
                        partnerMobile={option.mobile || ""}
                        fromLoansParty
                        partyName={option.address}
                      />
                    )}
                  </div>
                );
              })
            ) : (
              <div className="px-3 py-4 text-sm text-center">No data found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
